import React, { useState, useEffect } from 'react';
import { Card, Badge, DataTable, Input } from '../../../components/ui';
import { useAuth } from '../../../app/auth';
import {
  Search, FileText, Printer, RefreshCw,
  Banknote, Smartphone, ArrowRightLeft, CreditCard, ShoppingCart, X
} from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { procesarReimpresion, TicketPayload, TicketJugada } from '../../../services/ticketService';
import { TicketPreviewModal } from '../../pos/components/TicketPreviewModal';
import { formatTimeCR, fechaLocalYMD } from '../../../utils/date';

interface BackendJugada {
  id: string;
  ticket_id: string;
  sorteo_id: string;
  num1: string;
  num2: string | null;
  monto: string;
  estado: string;
}

interface BackendTicket {
  id: string;
  vendedor_id: string;
  local_id: string;
  sorteo_id: string;
  metodo_pago: string | null;
  referencia: string | null;
  total: string;
  estado: string;
  created_at: string;
  ticket_number?: string | null;
  jugadas: BackendJugada[];
}

interface BackendSorteo {
  id: string;
  tipo_id: string;
  nombre: string;
}

const METODOS = [
  { key: 'EFECTIVO', label: 'Efectivo', icon: Banknote, bg: 'bg-green-50 border-green-200', text: 'text-green-700', active: 'ring-2 ring-green-400 bg-green-100' },
  { key: 'SINPE', label: 'SINPE', icon: Smartphone, bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', active: 'ring-2 ring-blue-400 bg-blue-100' },
  { key: 'TRANSFERENCIA', label: 'Transfer.', icon: ArrowRightLeft, bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', active: 'ring-2 ring-purple-400 bg-purple-100' },
  { key: 'CREDITO', label: 'Crédito', icon: CreditCard, bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', active: 'ring-2 ring-orange-400 bg-orange-100' },
];

export const MisVentasPage: React.FC = () => {
  const { user } = useAuth();
  const vendedorId = user?.id || '';
  const hoy = fechaLocalYMD();
  const [fecha, setFecha] = useState(hoy);
  const [searchQuery, setSearchQuery] = useState('');
  const [tickets, setTickets] = useState<BackendTicket[]>([]);
  const [sorteosMap, setSorteosMap] = useState<Record<string, BackendSorteo>>({});
  const [loading, setLoading] = useState(true);
  const [filtroMetodo, setFiltroMetodo] = useState<string | null>(null);
  const [reprintPayload, setReprintPayload] = useState<TicketPayload | null>(null);
  const [reprintLoading, setReprintLoading] = useState<string | null>(null);

  const fetchTickets = () => {
    setLoading(true);
    Promise.all([
      fetchAPI<BackendTicket[]>(`/tickets?vendedor_id=${vendedorId}&fecha=${fecha}`),
      fetchAPI<BackendSorteo[]>('/sorteos'),
    ])
      .then(([ticketsData, sorteosData]) => {
        setTickets(ticketsData);
        const nextMap: Record<string, BackendSorteo> = {};
        for (const sorteo of sorteosData) {
          nextMap[sorteo.id] = sorteo;
        }
        setSorteosMap(nextMap);
      })
      .catch(err => console.error('Error cargando tickets:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); }, [fecha]);

  // Filtro por metodo + busqueda
  const filteredTickets = tickets.filter(t => {
    if (filtroMetodo && t.metodo_pago !== filtroMetodo) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const sorteoNombre = (sorteosMap[t.sorteo_id]?.nombre || '').toLowerCase();
    const sorteoTipo = (sorteosMap[t.sorteo_id]?.tipo_id || '').toLowerCase();
    return t.id.toLowerCase().includes(q) ||
      (t.ticket_number && t.ticket_number.toLowerCase().includes(q)) ||
      t.sorteo_id.toLowerCase().includes(q) ||
      sorteoNombre.includes(q) ||
      sorteoTipo.includes(q) ||
      t.vendedor_id.toLowerCase().includes(q);
  });

  // Solo tickets activos (no anulados) para el total
  const ticketsActivos = tickets.filter(t => t.estado !== 'ANULADO');
  const totalVentas = ticketsActivos.reduce((sum, t) => sum + parseFloat(t.total), 0);

  const porMetodo = (metodo: string) => {
    const filtered = ticketsActivos.filter(t => t.metodo_pago === metodo);
    return { count: filtered.length, total: filtered.reduce((s, t) => s + parseFloat(t.total), 0) };
  };

  const handleReprint = async (ticket: BackendTicket) => {
    setReprintLoading(ticket.id);
    try {
      const sorteo = sorteosMap[ticket.sorteo_id];
      const jugadas: TicketJugada[] = ticket.jugadas.map(j => ({
        num1: j.num1,
        num2: j.num2 || null,
        monto: parseFloat(j.monto),
      }));

      const payload = await procesarReimpresion({
        ticketCode: ticket.ticket_number || ticket.id,
        ticketId: ticket.id,
        sorteoNombre: sorteo?.nombre || ticket.sorteo_id,
        sorteoCierre: '',
        metodoPago: ticket.metodo_pago || 'EFECTIVO',
        referencia: ticket.referencia,
        total: parseFloat(ticket.total),
        jugadas,
        vendedorNombre: user?.nombre || '',
        localNombre: '',
        clienteNombre: (ticket as any).cliente_nombre || 'Cliente',
      });

      setReprintPayload(payload);
    } catch (err) {
      console.error('[MisVentas] Error reimpresión:', err);
    } finally {
      setReprintLoading(null);
    }
  };

  const columns = [
    {
      key: 'id',
      header: 'Ticket',
      render: (t: BackendTicket) => (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-400" />
          <span className="font-mono font-medium text-primary-600 text-xs">{t.ticket_number || t.id}</span>
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Hora',
      render: (t: BackendTicket) => formatTimeCR(t.created_at),
    },
    {
      key: 'sorteo',
      header: 'Sorteo',
      render: (t: BackendTicket) => {
        const sorteo = sorteosMap[t.sorteo_id];
        if (sorteo) {
          return <span className="text-xs text-gray-600">{sorteo.nombre}</span>;
        }
        return <span className="text-xs text-gray-600">{t.sorteo_id}</span>;
      },
    },
    {
      key: 'jugadas',
      header: 'Jugadas',
      render: (t: BackendTicket) => `${t.jugadas.length} num`,
    },
    {
      key: 'metodo_pago',
      header: 'Pago',
      render: (t: BackendTicket) => {
        const pago = t.metodo_pago || 'EFECTIVO';
        const colors: Record<string, string> = {
          EFECTIVO: 'success',
          SINPE: 'primary',
          TRANSFERENCIA: 'neutral',
          CREDITO: 'credit',
        };
        return <Badge variant={colors[pago] as any || 'neutral'}>{pago}</Badge>;
      },
    },
    {
      key: 'referencia',
      header: 'Referencia',
      render: (t: BackendTicket) => (
        <span className="font-mono text-xs text-gray-600">{t.referencia || '-'}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      render: (t: BackendTicket) => (
        <span className="font-bold text-gray-900">{'\u20A1'}{parseFloat(t.total).toLocaleString()}</span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (t: BackendTicket) => {
        const allAnuladas = t.jugadas.length > 0 && t.jugadas.every(j => j.estado === 'ANULADA');
        const estado = allAnuladas ? 'ANULADO' : 'VENDIDO';
        const color = allAnuladas ? 'danger' : 'success';
        return <Badge variant={color as any}>{estado}</Badge>;
      },
    },
    {
      key: 'actions',
      header: '',
      render: (t: BackendTicket) => (
        <button
          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
          onClick={() => handleReprint(t)}
          disabled={reprintLoading === t.id}
          title="Reimprimir ticket"
        >
          <Printer size={16} className={reprintLoading === t.id ? 'animate-pulse' : ''} />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Ventas</h1>
          <p className="text-gray-500">
            {new Date(fecha + 'T00:00:00').toLocaleDateString('es-CR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={fetchTickets}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Refrescar"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Banner total + banners por metodo */}
      <div
        className={`cursor-pointer rounded-xl p-3 text-white bg-gradient-to-br from-blue-600 to-blue-700 transition-all ${filtroMetodo === null ? 'ring-2 ring-blue-300 ring-offset-2' : 'opacity-80 hover:opacity-100'}`}
        onClick={() => setFiltroMetodo(null)}
      >
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-6 h-6" />
          <div className="flex-1">
            <p className="text-blue-100 text-xs">Total Ventas</p>
            <p className="text-xl font-bold">{'\u20A1'}{totalVentas.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-xs">Tickets</p>
            <p className="text-xl font-bold">{ticketsActivos.length}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {METODOS.map(m => {
          const data = porMetodo(m.key);
          const Icon = m.icon;
          const activo = filtroMetodo === m.key;
          return (
            <div
              key={m.key}
              className={`cursor-pointer rounded-lg border p-2 text-center transition-all ${activo ? m.active : m.bg} hover:shadow`}
              onClick={() => setFiltroMetodo(activo ? null : m.key)}
            >
              <div className="flex items-center justify-center gap-1">
                <Icon size={14} className={m.text} />
                <span className={`text-xs font-medium ${m.text}`}>{m.label}</span>
              </div>
              <p className={`text-sm font-bold ${m.text}`}>{'\u20A1'}{data.total.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{data.count} ticket{data.count !== 1 ? 's' : ''}</p>
            </div>
          );
        })}
      </div>

      {/* Filtro activo indicator + Search */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              placeholder="Buscar por ticket, sorteo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          {filtroMetodo && (
            <button
              onClick={() => setFiltroMetodo(null)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 px-3 py-2 border rounded-lg hover:bg-red-50"
            >
              <X size={14} /> Filtro: {METODOS.find(m => m.key === filtroMetodo)?.label}
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredTickets}
          keyExtractor={(t) => t.id}
          emptyMessage={loading ? 'Cargando...' : 'No hay ventas'}
        />
      </Card>

      {/* Reprint Preview Modal */}
      <TicketPreviewModal
        payload={reprintPayload}
        onClose={() => setReprintPayload(null)}
      />
    </div>
  );
};

export default MisVentasPage;
